import * as THREE from 'three';

import { OrthoTileModel } from '../../../../../typings/api/skymap/rest/v0/.common';
import { OrthoTileStore } from '../../../stores/geodata-store-factory/ortho-tile-store/ortho-tile-store';
import { CoordinateManager } from '../../coordinate-manager';
import { GlobalMercator } from '../../utilities/projections/global-mercator';
import { composeOrthotileHelpers, LocalBounds } from './compose-orthotile';

export type ComposeOrthotileDependencies = {
  orthotileProject: OrthoTileModel;
  globalMercator: GlobalMercator;
  store: OrthoTileStore;
  projection: string;
  /** Origo in project coordinates (same as CoordinateManager projection space). */
  origo: THREE.Vector3;
};

/**
 * Compose an orthophoto for the exact local meter bounds (no extra edge pixels).
 * Returns a PNG data URL.
 *
 * @param coordinateBoundaries - Bounds in local/scene coordinates (meters).
 * @param dependencies - Orthotile model, store, projection and origo (in project coords).
 * @param onProgress - Optional progress callback (0–1, optional label).
 */
export async function composeOrthotileWithHelpers(
  coordinateBoundaries: THREE.Box2,
  dependencies: ComposeOrthotileDependencies,
  onProgress?: (progress: number, label?: string) => void,
): Promise<string> {
  const { orthotileProject, globalMercator, store, projection, origo } = dependencies;

  const bounds: LocalBounds = {
    minX: coordinateBoundaries.min.x,
    minY: coordinateBoundaries.min.y,
    maxX: coordinateBoundaries.max.x,
    maxY: coordinateBoundaries.max.y,
  };

  const converter = {
    convert: (xLocal: number, yLocal: number) => {
      const { x: lon, y: lat } = CoordinateManager.getInstance().convertCoords(
        xLocal + origo.x,
        yLocal + origo.y,
        projection,
        'wgs84',
      );
      return { lon, lat };
    },
  };

  const MAX_PIXELS = 10_000_000;

  const canvas = await composeOrthotileHelpers.downloadHighResolutionOrthotile(
    bounds,
    orthotileProject,
    globalMercator,
    converter,
    store,
    MAX_PIXELS,
    undefined,
    onProgress,
  );

  return canvas.toDataURL('image/png');
}
